import React, { createContext, useContext, useEffect, useState } from "react";
import { DiscoveryData, DISCOVERY_API_ENDPOINTS, defaultDiscoveryData, ApplicationData } from "./constants";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../utils/axiosInstance";
import { message } from "antd";

interface DiscoveryContextState {
	discoveryData: DiscoveryData;
	loadingGetDiscoveryData?: boolean;
	loadingAddDiscoveryData: boolean;
	loading: boolean;
	loadingEditDiscoveryData: boolean;
	loadingGetApplicationData: boolean;
	applicationData: ApplicationData[];
	setDiscoveryData: React.Dispatch<React.SetStateAction<DiscoveryData>>;
	addDiscovery: () => void;
	updateDiscovery: () => void;
	clearData: () => void;
	getDetails: (id: string) => void;
	getApplicationData: () => void;
}

interface DiscoveryProviderProps {
	children: React.ReactNode;
}

const DiscoveryContext = createContext<DiscoveryContextState | undefined>(
	undefined
);

export const useDiscoveryContext = () => {
	const context = useContext(DiscoveryContext);
	if (!context) {
		throw new Error(
			"useDiscoveryContext must be used within an DiscoveryContextProvider"
		);
	}
	return context;
};

export const DiscoveryContextProvider: React.FC<DiscoveryProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [discoveryData, setDiscoveryData] = useState<DiscoveryData>(defaultDiscoveryData);
	const [applicationData, setApplicationData] = useState<ApplicationData[]>([]);

	const [loadingAddDiscoveryData, setLoadingAddDiscoveryData] = useState(false);
	const [loadingEditDiscoveryData, setLoadingEditDiscoveryData] = useState(false);
	const [loadingGetDiscoveryData, setLoadingGetDiscoveryData] = useState(false);
	const [loadingGetApplicationData, setLoadingGetApplicationData] = useState(false);

	const loading = loadingAddDiscoveryData || loadingEditDiscoveryData || loadingGetDiscoveryData;


	const isDataValid = (data: DiscoveryData) => {

		if (data.isVisible === undefined || data.displayOrder === undefined || !data.discoveryDetails || data.discoveryDetails.length === 0) {
			return false;
		}

		for (const detail of data.discoveryDetails) {
			if (!detail.title || !detail.description || !detail.culture) {
				return false;
			}
		}
		return true;
	}

	const clearData = () => {
		setDiscoveryData(defaultDiscoveryData);
	}

	const addDiscovery = async () => {
		setLoadingAddDiscoveryData(true);

		if (!isDataValid(discoveryData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingAddDiscoveryData(false);
			return;
		}

		try {
			const response = await axiosInstance.post(`${DISCOVERY_API_ENDPOINTS.ADD_DISCOVERY}`, discoveryData);
			if (response.data && response.data.isSuccessful) {
				message.success("İçerik başarıyla eklendi.");
			}
			setDiscoveryData(defaultDiscoveryData);
			setLoadingAddDiscoveryData(false);
		} catch (error) {
			console.error(error);
			setLoadingAddDiscoveryData(false);
			message.error("İçerik eklenirken bir hata oluştu.");
		} finally {
			navigate("/discovery");
		}
	};

	const updateDiscovery = async () => {
		setLoadingEditDiscoveryData(true);
		if (!isDataValid(discoveryData)) {
			message.info("Lütfen tüm alanları doldurun.");
			setLoadingEditDiscoveryData(false);
			return;
		}

		try {
			const response = await axiosInstance.put(`${DISCOVERY_API_ENDPOINTS.UPDATE_DISCOVERY}`, discoveryData);
			if (response.data && response.data.isSuccessful) {
				message.success("İçerik başarıyla güncellendi.");
			}
			setDiscoveryData(defaultDiscoveryData);
			setLoadingEditDiscoveryData(false);
		} catch (error) {
			console.error(error);
			setLoadingEditDiscoveryData(false);
			message.error("İçerik güncellenirken bir hata oluştu.");
		} finally {
			navigate("/discovery");
		}
	};

	const getDetails = async (id: string) => {
		setLoadingGetDiscoveryData(true);
		try {
			const response = await axiosInstance.get(`${DISCOVERY_API_ENDPOINTS.GET_DISCOVERY_DETAILS}?discoveryId=${id}`);
			console.log(response)
			if (response.data && response.data.isSuccessful) {
				setDiscoveryData(response.data.result.item);
			}
			setLoadingGetDiscoveryData(false);
		} catch (error) {
			console.error(error);
			setLoadingGetDiscoveryData(false);
		}
	};

	const getApplicationData = async () => {
		setLoadingGetApplicationData(true);
		try {
			const response = await axiosInstance.get(
				DISCOVERY_API_ENDPOINTS.GET_APPLICATİON_LIST
			);
			const data = response.data.result.result?.map(({ id, name }: any) => ({
				label: name,
				value: id,
			}))
			setApplicationData(data);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingGetApplicationData(false);
		}
	}

	const value: DiscoveryContextState = {
		discoveryData,
		loadingGetDiscoveryData,
		loadingAddDiscoveryData,
		loadingEditDiscoveryData,
		loading,
		loadingGetApplicationData,
		applicationData,
		setDiscoveryData,
		addDiscovery,
		clearData,
		updateDiscovery,
		getDetails,
		getApplicationData,
	};

	return (
		<DiscoveryContext.Provider value={value}>
			{children}
		</DiscoveryContext.Provider>
	);
};
